// Client-facing portal — public read-only transaction view accessed via /portal/:token.
// No auth required; token is verified against Firestore via a Cloud Function.

import React from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../firebase';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { beginQuietReloadBusy, endQuietReloadBusy } from '../../../hooks/useQuietReload';
import SellerNetSheetTab from '../transactions/SellerNetSheetTab';
import OverviewClosingPath from '../transactions/v2/OverviewClosingPath';
import { buildPortalClosingPathFormData } from './portalClosingPath';
import WorkspaceLogo from './WorkspaceLogo';
import PillTabRail from '../../ui/PillTabRail';
import PortalFilesCard from './PortalFilesCard';
import SessionExpiredOverlay from './SessionExpiredOverlay';
import ChecklistSection from './ChecklistSection';
import PortalStatusBar from './PortalStatusBar';
import PortalGlanceStrip from './PortalGlanceStrip';
import PortalAgentCard from './PortalAgentCard';
import PortalTeamCard from './PortalTeamCard';
import PortalDetailsCard from './PortalDetailsCard';
import PortalLanguageToggle from './PortalLanguageToggle';
import { PortalLanguageContext, usePortalLanguageState } from '../../../contexts/PortalLanguageContext';
import {
    formatClientPortalDateOnly,
    getClientPortalTokenFromPath,
    normalizeClientPortalLastName
} from '../../../utils/clientPortal';

// Lazy chunk: this page is statically imported by App.js, so a static import here
// would land the Emails tab in the entry bundle.
const PortalEmailsTab = React.lazy(() => import('./PortalEmailsTab'));

const functions = getFunctions(app, 'us-central1');

const readPortalAccess = httpsCallable(functions, 'verifyClientPortalAccess');

const callPortalFunction = (name) => httpsCallable(functions, name);

const resolveErrorMessage = (error) => {
    const detailMessage = typeof error?.details === 'string' ? error.details.trim() : '';
    if (detailMessage) return detailMessage;
    const message = (error?.message || '')
        .replace(/^internal\s*/i, '')
        .replace(/^permission-denied\s*/i, '')
        .replace(/^not-found\s*/i, '')
        .replace(/^invalid-argument\s*/i, '')
        .trim();
    if (message) return message;
    if (typeof error?.code === 'string' && error.code.startsWith('functions/')) {
        return error.code.replace('functions/', '').replace(/-/g, ' ');
    }
    return 'Unable to verify portal access.';
};

function ClientPortalPage({ token: tokenProp = '' }) {
    const isCompactLayout = useMediaQuery('(max-width: 1023px)');
    const { i18n, setLang } = usePortalLanguageState();
    const { t } = i18n;
    const token = React.useMemo(
        () => (tokenProp || getClientPortalTokenFromPath(typeof window !== 'undefined' ? window.location.pathname : '')).trim(),
        [tokenProp]
    );
    const [lastName, setLastName] = React.useState('');
    const [verificationError, setVerificationError] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [portalData, setPortalData] = React.useState(null);
    const [loadingStage, setLoadingStage] = React.useState(0);

    const getFileUrl = React.useMemo(() => callPortalFunction('getClientPortalFileUrl'), []);

    const [expired, setExpired] = React.useState(false);
    const [reverifyError, setReverifyError] = React.useState('');
    const [reverifyLoading, setReverifyLoading] = React.useState(false);

    // The only portal write: the busy counter defers the quiet reload (D-16) while it is in flight.
    const updateChecklistItem = React.useMemo(() => {
        const call = callPortalFunction('updateClientPortalChecklistItem');
        return async (payload) => {
            beginQuietReloadBusy();
            try {
                return await call(payload);
            } finally {
                endQuietReloadBusy();
            }
        };
    }, []);

    // The visible email list lives in its own state and is never part of portalData,
    // so the sessionStorage cache below cannot persist it (T-61-54).
    const [portalEmails, setPortalEmails] = React.useState(null);

    const [activeMainTab, setActiveMainTab] = React.useState('overview');
    const showNetSheetTab = portalData?.transaction?.transactionType === 'Seller' && Boolean(portalData?.sellerNetSheet);
    const showEmailsTab = Array.isArray(portalEmails) && portalEmails.length > 0;
    React.useEffect(() => {
        if (!showNetSheetTab && activeMainTab === 'netsheet') setActiveMainTab('overview');
        if (!showEmailsTab && activeMainTab === 'emails') setActiveMainTab('overview');
    }, [showNetSheetTab, showEmailsTab, activeMainTab]);

    const sessionJwt = portalData?.sessionToken?.jwt || '';
    React.useEffect(() => {
        if (!sessionJwt || expired) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const result = await callPortalFunction('listPortalEmails')({ sessionToken: sessionJwt });
                if (cancelled) return;
                setPortalEmails(Array.isArray(result?.data?.emails) ? result.data.emails : []);
            } catch (error) {
                if (cancelled) return;
                const code = String(error?.code || '');
                if (code === 'unauthenticated' || code === 'functions/unauthenticated') {
                    setExpired(true);
                    return;
                }
                console.error('Failed to load portal emails:', error);
                setPortalEmails([]);
            }
        })();
        return () => { cancelled = true; };
    }, [sessionJwt, expired]);

    // Stale list after session end must not linger (T-61-55).
    React.useEffect(() => {
        if (expired) setPortalEmails(null);
    }, [expired]);

    const loadingStages = [
        { label: t('Verifying link…'), progress: 25 },
        { label: t('Loading transaction…'), progress: 65 },
        { label: t('Almost ready…'), progress: 92 }
    ];

    React.useEffect(() => {
        if (!isLoading) {
            setLoadingStage(0);
            return undefined;
        }
        const t1 = setTimeout(() => setLoadingStage(1), 1200);
        const t2 = setTimeout(() => setLoadingStage(2), 2800);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [isLoading]);

    React.useEffect(() => {
        const { body, documentElement } = document;
        const previousBodyOverflow = body.style.overflow;
        const previousHtmlOverflow = documentElement.style.overflow;
        const previousBodyBg = body.style.backgroundColor;

        body.style.overflow = 'auto';
        documentElement.style.overflow = 'auto';
        body.style.backgroundColor = 'var(--body-bg)';

        return () => {
            body.style.overflow = previousBodyOverflow;
            documentElement.style.overflow = previousHtmlOverflow;
            body.style.backgroundColor = previousBodyBg;
        };
    }, []);

    React.useEffect(() => {
        if (portalData?.sessionToken) {
            try { window.sessionStorage.setItem('portalSession', JSON.stringify({ portalData, savedAt: Date.now() })); } catch { /* sessionStorage unavailable (private mode or quota); persistence is best-effort */ }
        }
    }, [portalData]);

    React.useEffect(() => {
        try {
            const raw = window.sessionStorage.getItem('portalSession');
            if (!raw) return;
            const parsed = JSON.parse(raw);
            const exp = parsed?.portalData?.sessionToken?.expiresAt;
            if (exp && new Date(exp).getTime() > Date.now()) {
                setPortalData(parsed.portalData);
            } else {
                window.sessionStorage.removeItem('portalSession');
            }
        } catch { /* ignore */ }
    }, []);

    React.useEffect(() => {
        const exp = portalData?.sessionToken?.expiresAt;
        if (!exp) return undefined;
        const ms = new Date(exp).getTime() - Date.now();
        if (ms <= 0) {
            setExpired(true);
            return undefined;
        }
        const timer = setTimeout(() => setExpired(true), ms);
        return () => clearTimeout(timer);
    }, [portalData?.sessionToken?.expiresAt]);

    const handleReverify = React.useCallback(async (newLastName) => {
        if (!token) return;
        setReverifyError('');
        setPortalEmails(null);
        setReverifyLoading(true);
        try {
            const response = await readPortalAccess({ token, lastName: normalizeClientPortalLastName(newLastName) });
            const fresh = response.data || null;
            if (!fresh) throw new Error('No data returned.');
            setPortalData(fresh);
            setExpired(false);

            // Flush queued toggles
            const raw = window.sessionStorage.getItem('portalPendingToggles');
            if (raw) {
                const queued = JSON.parse(raw);
                window.sessionStorage.removeItem('portalPendingToggles');
                await Promise.allSettled(
                    Object.entries(queued).map(([itemId, { completed }]) =>
                        updateChecklistItem({ sessionToken: fresh.sessionToken?.jwt, itemId, completed })
                    )
                );
            }
        } catch (error) {
            setReverifyError(resolveErrorMessage(error));
        } finally {
            setReverifyLoading(false);
        }
    }, [token, updateChecklistItem]);

    const handleChecklistItemPersisted = React.useCallback((itemId, completed) => {
        setPortalData(prev => {
            if (!prev) return prev;
            const checklists = (prev.checklists || []).map(cl => {
                const nextState = { ...(cl.completionState || {}) };
                if (completed) {
                    nextState[itemId] = { completedAt: Date.now(), completedFromPortal: true };
                } else {
                    delete nextState[itemId];
                }
                return { ...cl, completionState: nextState };
            });
            return { ...prev, checklists };
        });
    }, []);

    const handleVerify = async (event) => {
        event.preventDefault();
        const normalizedLastName = normalizeClientPortalLastName(lastName);
        if (!token) {
            setVerificationError('Portal link is invalid.');
            return;
        }
        if (!normalizedLastName) {
            setVerificationError('Enter the client last name to continue.');
            return;
        }
        setIsLoading(true);
        setVerificationError('');
        try {
            const response = await readPortalAccess({ token, lastName: normalizedLastName });
            setPortalData(response.data || null);
        } catch (error) {
            console.error('Failed to verify client portal access:', error);
            setVerificationError(resolveErrorMessage(error));
            setPortalData(null);
        } finally {
            setIsLoading(false);
        }
    };

    const addressLine = [
        portalData?.transaction?.propertyStreet,
        portalData?.transaction?.propertyCityStateZip
    ].filter(Boolean).join(', ') || portalData?.transaction?.propertyAddress || t('Transaction Portal');

    const closingPathFormData = React.useMemo(
        () => buildPortalClosingPathFormData(portalData),
        [portalData]
    );

    const clientNamesLine = React.useMemo(() => {
        const names = Array.isArray(portalData?.clientNames) ? portalData.clientNames.filter(Boolean) : [];
        if (names.length === 0) return '';
        if (names.length === 1) return names[0];
        if (names.length === 2) return `${names[0]} & ${names[1]}`;
        return `${names.slice(0, -1).join(', ')}, & ${names[names.length - 1]}`;
    }, [portalData?.clientNames]);

    const portalTabs = [
        { value: 'overview', label: t('Checklists & Files') },
        ...(showNetSheetTab ? [{ value: 'netsheet', label: t('Seller Net Sheet') }] : []),
        ...(showEmailsTab ? [{ value: 'emails', label: t('Emails') }] : [])
    ];

    return (
        <PortalLanguageContext.Provider value={i18n}>
        <div className={`min-h-screen ${isCompactLayout ? 'px-4 py-6' : 'px-4 py-10 sm:px-6 lg:px-8'}`}>
            <div className="mx-auto w-full max-w-[1600px]">
                <div className="mb-4 flex justify-end">
                    <PortalLanguageToggle onChange={setLang} />
                </div>
                {!portalData ? (
                    <div className="mx-auto max-w-lg overflow-hidden rounded-2xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] shadow-md">
                        <div
                            data-testid="portal-verify-band"
                            style={{ backgroundImage: 'var(--gradient-header)' }}
                            className={`text-center text-[color:var(--header-text-color)] ${isCompactLayout ? 'px-5 py-6' : 'px-8 py-8'}`}
                        >
                            <div className="mx-auto mb-4 inline-block rounded-xl bg-[color:var(--surface-primary)] px-4 py-2">
                                <img
                                    src="/images/keyflow-logo.generated.svg"
                                    alt="KeyFlow"
                                    className="h-12"
                                />
                            </div>
                            <p className="flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: 'color-mix(in srgb, var(--color-accent), var(--header-text-color) 60%)' }}>
                                <span aria-hidden="true" className="h-0.5 w-6 rounded-full bg-[color:var(--color-accent)]" />
                                {t('Client Portal')}
                                <span aria-hidden="true" className="h-0.5 w-6 rounded-full bg-[color:var(--color-accent)]" />
                            </p>
                            <h1 className={`mt-2 font-semibold text-[color:var(--header-text-color)] ${isCompactLayout ? 'text-2xl' : 'text-3xl'}`}>{t('Verify access')}</h1>
                        </div>
                        <div className={isCompactLayout ? 'p-5' : 'p-8'}>
                        <p className="mb-6 text-center text-sm text-[color:var(--text-muted)]">{t('Enter the client last name associated with this transaction to continue.')}</p>
                        {isLoading ? (
                            <div className="space-y-4 py-2" role="status" aria-live="polite">
                                <div className="h-2 w-full overflow-hidden rounded-full bg-[color:var(--surface-subtle)]">
                                    <div
                                        className="h-full rounded-full bg-[color:var(--color-accent)] transition-all duration-700 ease-out"
                                        style={{ width: `${loadingStages[loadingStage].progress}%` }}
                                    />
                                </div>
                                <p className="text-center text-sm text-[color:var(--text-muted)]">{loadingStages[loadingStage].label}</p>
                            </div>
                        ) : (
                            <form className="space-y-4" onSubmit={handleVerify}>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-[color:var(--text-muted)]">{t('Client last name')}</label>
                                    <input
                                        type="text"
                                        value={lastName}
                                        onChange={(event) => setLastName(event.target.value)}
                                        className="w-full rounded-md border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] text-[color:var(--text-primary)] px-3 py-3"
                                        autoComplete="family-name"
                                    />
                                </div>
                                {verificationError && (
                                    <div className="rounded-md border border-[color:var(--color-danger)]/20 bg-[color:var(--surface-danger)] px-3 py-2 text-sm text-[color:var(--color-danger)]">
                                        {t(verificationError)}
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    className="w-full min-h-[44px] rounded-md bg-[color:var(--color-primary)] px-4 py-3 text-sm font-semibold text-[color:var(--on-primary)] hover:bg-[color:var(--color-primary-hover)]"
                                >
                                    {t('Continue')}
                                </button>
                            </form>
                        )}
                        </div>
                    </div>
                ) : (
                    <div className={expired ? 'pointer-events-none blur-sm select-none' : ''}>
                    <div className="space-y-6">
                        <section
                            data-tour="portal-header"
                            data-testid="portal-banner"
                            style={{ backgroundImage: 'var(--gradient-header)' }}
                            className={`rounded-2xl border border-[color:var(--header-chip-border)] text-[color:var(--header-text-color)] shadow-md ${isCompactLayout ? 'p-5' : 'p-6 sm:p-8'}`}
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                <div className="self-start rounded-xl bg-[color:var(--surface-primary)] px-3 py-2 sm:self-center">
                                    <WorkspaceLogo branding={portalData?.branding} className="h-10 w-auto max-w-[200px] sm:h-14" />
                                </div>
                                <div className="sm:ml-auto sm:text-right">
                                    <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] sm:justify-end" style={{ color: 'color-mix(in srgb, var(--color-accent), var(--header-text-color) 60%)' }}>
                                        <span aria-hidden="true" className="h-0.5 w-6 rounded-full bg-[color:var(--color-accent)]" />
                                        {t('Client Portal')}
                                    </p>
                                    <h1 className={`mt-1 font-semibold text-[color:var(--header-text-color)] ${isCompactLayout ? 'text-2xl' : 'text-3xl'}`}>{addressLine}</h1>
                                    {clientNamesLine && (
                                        <p className="mt-1 text-sm font-medium text-[color:var(--header-text-color)]">{t('Prepared for {names}', { names: clientNamesLine })}</p>
                                    )}
                                    {portalData?.portal?.expiresAt && (
                                        <p className="mt-1 text-sm text-[color:var(--header-text-color)]">{t('Link expires {date}.', { date: formatClientPortalDateOnly(portalData.portal.expiresAt, { locale: i18n.locale }) })}</p>
                                    )}
                                </div>
                            </div>
                            <PortalStatusBar
                                transaction={closingPathFormData}
                                completedMilestones={portalData?.completedMilestones}
                            />
                            <PortalGlanceStrip transaction={closingPathFormData} />
                        </section>

                        <section className="grid gap-6 xl:grid-cols-[280px_minmax(0,1fr)]">
                            <div data-tour="portal-agent" className="space-y-6">
                                <PortalAgentCard
                                    agentContact={portalData?.agentContact}
                                    transaction={closingPathFormData}
                                    addressLine={addressLine}
                                    workspaceName={portalData?.branding?.workspaceName || ''}
                                    isCompactLayout={isCompactLayout}
                                />

                                <PortalTeamCard
                                    teamContacts={portalData?.teamContacts}
                                    isCompactLayout={isCompactLayout}
                                />

                                <OverviewClosingPath
                                    formData={closingPathFormData}
                                    initialData={closingPathFormData}
                                    logEntries={[]}
                                />
                            </div>

                            <div data-tour="portal-checklists" className="space-y-6">
                                {portalTabs.length > 1 && (
                                    <PillTabRail
                                        tabs={portalTabs}
                                        activeValue={activeMainTab}
                                        onChange={setActiveMainTab}
                                        ariaLabel={t('Portal sections')}
                                    />
                                )}

                                {activeMainTab === 'overview' && (
                                    <>
                                        <PortalDetailsCard
                                            transaction={closingPathFormData}
                                            isCompactLayout={isCompactLayout}
                                        />
                                        {(portalData?.checklists || []).length > 0 && (
                                            <div>
                                                <div className="mb-4 flex items-start gap-3">
                                                    <div className="flex-shrink-0 rounded-lg bg-[color:var(--surface-info)] p-2 text-[color:var(--color-accent)]">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                            <path d="M9 11l3 3L22 4" />
                                                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{t('Helpful Checklists')}</h2>
                                                        <p className="mt-1 text-sm text-[color:var(--text-muted)]">{t("These checklists are here to help you stay organized — they're optional, not required. Check items off as you go if it's useful for you.")}</p>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                                                    {portalData.checklists.map(checklist => (
                                                        <ChecklistSection
                                                            key={checklist.id}
                                                            checklist={checklist}
                                                            sessionToken={portalData?.sessionToken}
                                                            updateItem={(payload) => updateChecklistItem(payload).then(r => r.data)}
                                                            onSessionExpired={() => setExpired(true)}
                                                            onItemPersisted={handleChecklistItemPersisted}
                                                            isCompactLayout={isCompactLayout}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <PortalFilesCard
                                            files={portalData?.files || []}
                                            sessionToken={portalData?.sessionToken}
                                            getFileUrl={getFileUrl}
                                            onSessionExpired={() => setExpired(true)}
                                            isCompactLayout={isCompactLayout}
                                        />
                                    </>
                                )}

                                {activeMainTab === 'netsheet' && showNetSheetTab && (
                                    <SellerNetSheetTab
                                        formData={{
                                            ...portalData.transaction,
                                            sellerNetSheet: portalData.sellerNetSheet
                                        }}
                                        readOnly
                                        variant="portal"
                                        maxScenarios={3}
                                    />
                                )}

                                {activeMainTab === 'emails' && showEmailsTab && (
                                    <React.Suspense
                                        fallback={(
                                            <div className="space-y-3" role="status" aria-label={t('Loading emails')}>
                                                {[0, 1, 2].map((i) => (
                                                    <div key={i} className="h-16 rounded-xl bg-[color:var(--surface-subtle)]" />
                                                ))}
                                            </div>
                                        )}
                                    >
                                        <PortalEmailsTab
                                            emails={portalEmails}
                                            sessionToken={portalData.sessionToken}
                                            callPortalFunction={callPortalFunction}
                                            onSessionExpired={() => setExpired(true)}
                                            isCompactLayout={isCompactLayout}
                                        />
                                    </React.Suspense>
                                )}
                            </div>
                        </section>
                    </div>
                    </div>
                )}
                {expired && portalData && (
                    <SessionExpiredOverlay
                        token={token}
                        onReverify={handleReverify}
                        isLoading={reverifyLoading}
                        error={t(reverifyError)}
                    />
                )}
            </div>
        </div>
        </PortalLanguageContext.Provider>
    );
}

export default ClientPortalPage;
